import { useState, useEffect, useMemo } from "react";
import { CheckIcon } from "../components/Icons";
import { Link, useLocation } from "react-router-dom";
import { ProductImageWithFallbacks } from "../components/ProductImageWithFallbacks";
import { fetchProductsFromSheet } from "../utils/sheetProducts";
import { buildShopListings, getVariantSizes, getVariantColors, listingNeedsVariantPick, pickListingRepresentative } from "../utils/groupProducts";
import { ProductPriceBlock } from "../components/ProductPriceBlock";
import ShopVariantModal from "../components/ShopVariantModal";
import { BtnContent } from "../components/BtnIcon";
import {
  IoCartOutline,
  IoCloseOutline,
  IoFilterOutline,
  IoRefreshOutline,
} from "react-icons/io5";
import "../styles/product-variant-picker.css";

function Shop({ addToCart }) {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const [filter, setFilter] = useState({
    color: "",
    fabric: "",
    sort: "",
    search: "",
    groupBy: "color",
  });
  const [variantModalEntry, setVariantModalEntry] = useState(null);
  const [variantModalSelection, setVariantModalSelection] = useState(null);
  const [addedListingKey, setAddedListingKey] = useState(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    fetchProductsFromSheet()
      .then((formatted) => {
        setProducts(formatted);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoadError(err?.message || "Could not load products.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!location.state?.filter) return;
    setFilter((current) => ({ ...current, ...location.state.filter }));
  }, [location.state]);

  const listings = useMemo(
    () => buildShopListings(products, filter.groupBy || "none"),
    [products, filter.groupBy],
  );

  const filteredListings = listings
    .filter((entry) => {
      const product = entry.representative;
      const matchesSearch =
        !filter.search ||
        product.name.toLowerCase().includes(filter.search.toLowerCase()) ||
        product.description.toLowerCase().includes(filter.search.toLowerCase()) ||
        entry.variants.some(
          (v) =>
            v.name.toLowerCase().includes(filter.search.toLowerCase()) ||
            v.description.toLowerCase().includes(filter.search.toLowerCase()),
        );

      const matchesColor =
        !filter.color ||
        entry.variants.some((v) => v.color === filter.color);

      const matchesFabric =
        !filter.fabric ||
        entry.variants.some((v) => v.fabric === filter.fabric || v.boxSizeLabel === filter.fabric);

      return matchesSearch && matchesColor && matchesFabric;
    })
    .sort((a, b) => {
      if (filter.sort === "price-low") return a.representative.price - b.representative.price;
      if (filter.sort === "price-high") return b.representative.price - a.representative.price;
      return 0;
    });

  const uniqueColors = [...new Set(products.map((p) => p.color).filter(Boolean))];
  const uniqueFabrics = [...new Set(products.map((p) => p.fabric).filter(Boolean))];

  const handleAddToCart = (entry, event) => {
    event.preventDefault();
    event.stopPropagation();

    const product = entry.representative;

    if (listingNeedsVariantPick(entry)) {
      const initial = pickListingRepresentative(entry.variants) || product;
      setVariantModalEntry(entry);
      setVariantModalSelection(initial);
      return;
    }

    addToCart(product);
    setAddedListingKey(entry.listingKey);
    setTimeout(() => setAddedListingKey(null), 1500);
  };

  const handleVariantModalConfirm = (variant, quantity = 1) => {
    if (!variant || (variant.stock || 0) <= 0) return;
    addToCart(variant, { quantity });
    const key = variantModalEntry?.listingKey;
    setVariantModalEntry(null);
    setVariantModalSelection(null);
    if (key) {
      setAddedListingKey(key);
      setTimeout(() => setAddedListingKey(null), 1500);
    }
  };

  const closeVariantModal = () => {
    setVariantModalEntry(null);
    setVariantModalSelection(null);
  };

  const entryHasStock = (entry) =>
    entry.variants.some((v) => (v.stock || 0) > 0);

  const clearFilters = () =>
    setFilter({ color: "", fabric: "", sort: "", search: "", groupBy: filter.groupBy });

  const productLinkState = (entry) => ({
    product: entry.representative,
    variants: entry.variants,
    groupBy: entry.groupBy,
  });

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading our gift collection...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container py-5" style={{ maxWidth: "640px" }}>
        <h1 className="section-title">Gift Box Collection</h1>
        <div className="alert alert-warning" role="alert">
          {loadError}
        </div>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => window.location.reload()}
        >
          <BtnContent icon={IoRefreshOutline}>Try again</BtnContent>
        </button>
      </div>
    );
  }

  return (
    <div className="container container--shop">
      <ShopVariantModal
        entry={variantModalEntry}
        selectedVariant={variantModalSelection}
        onSelect={setVariantModalSelection}
        onConfirm={handleVariantModalConfirm}
        onClose={closeVariantModal}
      />

      <h1 className="section-title">Gift Box Collection</h1>

      <div className="shop-layout">
        {/* FILTERS */}
        <div className={`filters ${showFilters ? "show" : ""}`}>
          <button
            className="close-filter"
            onClick={() => setShowFilters(false)}
            aria-label="Close filters"
          >
            <IoCloseOutline size={20} aria-hidden />
          </button>

          <div className="filters__search">
            <h3>Filters</h3>
            <input
              type="text"
              placeholder="Search gift boxes..."
              value={filter.search}
              onChange={(e) =>
                setFilter({ ...filter, search: e.target.value })
              }
            />
          </div>

          <div className="filters__scroll">
            <select
              value={filter.groupBy}
              onChange={(e) => setFilter({ ...filter, groupBy: e.target.value })}
            >
              <option value="none">Each variant (no grouping)</option>
              <option value="color">Group by color</option>
              <option value="size">Group by size</option>
            </select>

            <select
              value={filter.sort}
              onChange={(e) => setFilter({ ...filter, sort: e.target.value })}
            >
              <option value="">Sort By</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>

            <select
              value={filter.color}
              onChange={(e) => setFilter({ ...filter, color: e.target.value })}
            >
              <option value="">All Colors</option>
              {uniqueColors.map((color) => (
                <option key={color}>{color}</option>
              ))}
            </select>

            <select
              value={filter.fabric}
              onChange={(e) => setFilter({ ...filter, fabric: e.target.value })}
            >
              <option value="">All box sizes</option>
              {uniqueFabrics.map((fabric) => (
                <option key={fabric}>{fabric}</option>
              ))}
            </select>

            <button
              className="btn btn-secondary"
              onClick={clearFilters}
            >
              <BtnContent icon={IoRefreshOutline}>Clear Filters</BtnContent>
            </button>
          </div>
        </div>

        {/* PRODUCTS */}
        <div className="products-section">
          <button
            className="mobile-filter-btn"
            onClick={() => setShowFilters(true)}
          >
            <BtnContent icon={IoFilterOutline}>Filters</BtnContent>
          </button>

          {filteredListings.length === 0 ? (
            <div className="empty-cart">
              <h2>No products found</h2>
              <button
                className="btn btn-primary"
                onClick={clearFilters}
              >
                <BtnContent icon={IoRefreshOutline}>Show All</BtnContent>
              </button>
            </div>
          ) : (
            <>
              <p style={{ textAlign: "center", marginBottom: "2rem" }}>
                Showing {filteredListings.length} listing{filteredListings.length === 1 ? "" : "s"}
                {filter.groupBy === "none" ? ` of ${products.length} products` : ` (${products.length} variants in catalog)`}
              </p>

              <div className="products-grid">
                {filteredListings.map((entry) => {
                  const product = entry.representative;
                  const sizes = getVariantSizes(entry.variants);
                  const colors = getVariantColors(entry.variants);
                  const cardTitle =
                    entry.groupBy === "size" && entry.displaySize
                      ? entry.displaySize
                      : product.name;

                  return (
                    <div className="product-card" key={entry.listingKey}>
                      <Link
                        to={`/product/${product.id}`}
                        state={productLinkState(entry)}
                      >
                        <ProductImageWithFallbacks
                          urls={product.images}
                          alt={product.name}
                          loading="lazy"
                        />
                      </Link>

                      <div className="product-info">
                        <Link
                          to={`/product/${product.id}`}
                          state={productLinkState(entry)}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <h3>{cardTitle}</h3>
                        </Link>

                        <div className="product-meta">
                          {entry.groupBy === "color" && entry.displayColor ? (
                            <span>● {entry.displayColor}</span>
                          ) : null}

                          {entry.groupBy === "none" && product.color ? (
                            <span>● {product.color}</span>
                          ) : null}

                          {entryHasStock(entry) && (
                            <span style={{ color: "green" }}>
                              <CheckIcon size={16} /> In Stock
                            </span>
                          )}
                        </div>

                        {entry.groupBy === "size" && colors.length > 0 ? (
                          <div className="product-card__sizes">
                            <span className="product-card__sizes-label">Colors:</span>
                            <div className="product-card__sizes-list">
                              {colors.map((color) => {
                                const variant = entry.variants.find((v) => v.color === color);
                                const outOfStock = variant && (variant.stock || 0) <= 0;
                                return (
                                  <span
                                    key={color}
                                    className={`product-card__size-chip${outOfStock ? " is-out-of-stock" : ""}`}
                                  >
                                    {color}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}

                        {entry.groupBy !== "size" && sizes.length > 0 ? (
                          <div className="product-card__sizes">
                            <span className="product-card__sizes-label">Sizes:</span>
                            <div className="product-card__sizes-list">
                              {sizes.map((size) => {
                                const variant = entry.variants.find(
                                  (v) => (v.boxSizeLabel || v.fabric) === size,
                                );
                                const outOfStock = variant && (variant.stock || 0) <= 0;
                                return (
                                  <span
                                    key={size}
                                    className={`product-card__size-chip${outOfStock ? " is-out-of-stock" : ""}`}
                                  >
                                    {size}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}

                        <p className="product-description">
                          {product.description}
                        </p>

                        <div className="product-price">
                          <ProductPriceBlock product={product} variant="shop" />
                        </div>

                        <button
                          className="btn"
                          onClick={(e) => handleAddToCart(entry, e)}
                          disabled={!entryHasStock(entry)}
                        >
                          {!entryHasStock(entry) ? (
                            "Out of Stock"
                          ) : addedListingKey === entry.listingKey ? (
                            "✔ Added"
                          ) : (
                            <BtnContent icon={IoCartOutline}>Add to Cart</BtnContent>
                          )}
                        </button>
                        <div className="mt-2">
                          <Link
                            to={`/customise/${product.id}`}
                            state={{ product }}
                            className="small text-decoration-none"
                            style={{ color: "var(--accent-primary)" }}
                          >
                            Add message on gift box
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Shop;
